/**
 * A page in tabs, one per question a reader brings, with the tab in the address (?view=...). The pattern Leadership
 * set in 5B: an accessible tablist where arrow keys, Home and End move between tabs and only the selected tab is in
 * the tab order; each change is a history entry, built from the live address so two quick changes never work from an
 * older query; an old or unknown view is rewritten without a history entry.
 *
 * ViewTabs is the one tablist for every page in tabs (5D integration, 2026-10-07): Leadership, the National view, How
 * National can help, the control page and Each control. Leadership, the National view and the control page (?tab=,
 * UX-06) take their tab from useViewParam. How National can help keeps its own address rule (it reads "#ask" and
 * "?ask=" and rewrites them), and Each control picks its default from the reader.
 */
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface ViewTab<V extends string> {
  id: V;
  label: string;
  /** The label on phones, when the full one would push the row past the screen (UX-05). */
  short?: string;
  question: string;
}

/**
 * The tab in the address, its setter, and the rewrite of an old tab name (no history entry). `param` is the query
 * key (`view` on most pages, `tab` on the control page); `fallback` is the tab an address without one opens.
 */
export function useViewParam<V extends string>(
  views: ReadonlyArray<{ id: V; label: string }>,
  fallback: V,
  aliases: Readonly<Record<string, V>> = {},
  param = 'view',
) {
  const [params, setParams] = useSearchParams();
  const raw = params.get(param);
  const known = (v: string | null): v is V => views.some((x) => x.id === v);
  const view: V = known(raw) ? raw : (raw && aliases[raw]) || fallback;
  useEffect(() => {
    if (raw && !known(raw) && aliases[raw]) {
      const next = new URLSearchParams(window.location.search);
      next.set(param, aliases[raw]!);
      setParams(next, { replace: true });
    }
    // `known` reads `views`, which is a module constant for every caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw, setParams, param]);
  const setView = (v: V) => {
    const next = new URLSearchParams(window.location.search);
    next.set(param, v);
    if (next.toString() === new URLSearchParams(window.location.search).toString()) return;
    setParams(next);
  };
  /** The view the live address names, for key presses that land before the next render. */
  const liveView = (): V => {
    const live = new URLSearchParams(window.location.search).get(param);
    return known(live) ? live : (live && aliases[live]) || fallback;
  };
  return { view, setView, liveView };
}

/**
 * The tablist. `idPrefix` names the tabs (`${idPrefix}-tab-${id}`) and the panel (`${idPrefix}-panel`, or
 * `panelId(id)` for a page with one panel per tab). `liveView` (default: `view`) is the tab the live address names,
 * so a key press that lands before the next render moves from the right tab.
 */
export function ViewTabs<V extends string>({ views, view, onSelect, label, idPrefix, liveView, panelId, className }: {
  /** `short`: the label on phones (UX-05), so the row fits on one line; the tab keeps its full name for everyone. */
  views: ReadonlyArray<{ id: V; label: string; short?: string }>;
  view: V;
  onSelect: (v: V) => void;
  label: string;
  idPrefix: string;
  liveView?: () => V;
  panelId?: (v: V) => string;
  className?: string;
}) {
  useEffect(() => {
    const tab = document.getElementById(`${idPrefix}-tab-${view}`);
    const list = tab?.parentElement;
    if (!tab || !list) return;
    const left = tab.offsetLeft - list.scrollLeft;
    if (left < 0 || left + tab.offsetWidth > list.clientWidth) list.scrollLeft = tab.offsetLeft;
  }, [view, idPrefix]);
  const move = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const i = Math.max(0, views.findIndex((x) => x.id === (liveView ? liveView() : view)));
    const to = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1 : e.key === 'Home' ? 0 : e.key === 'End' ? views.length - 1 : null;
    if (to === null) return;
    e.preventDefault();
    const next = views[(to + views.length) % views.length]!;
    onSelect(next.id);
    requestAnimationFrame(() => document.getElementById(`${idPrefix}-tab-${next.id}`)?.focus());
  };
  return (
    <div className={className ? `lead-tabs ${className}` : 'lead-tabs'} role="tablist" aria-label={label} onKeyDown={move}>
      {views.map((v) => (
        <button
          key={v.id}
          id={`${idPrefix}-tab-${v.id}`}
          type="button"
          role="tab"
          aria-selected={v.id === view}
          aria-controls={panelId ? panelId(v.id) : `${idPrefix}-panel`}
          tabIndex={v.id === view ? 0 : -1}
          className={v.id === view ? 'lead-tab selected' : 'lead-tab'}
          onClick={() => onSelect(v.id)}
          aria-label={v.short ? v.label : undefined}
        >
          {/* The short label is CSS-drawn on phones (data-short), so the tab's text and name stay its full label. */}
          {v.short ? (
            <span className="tab-full" data-short={v.short}>
              {v.label}
            </span>
          ) : (
            v.label
          )}
        </button>
      ))}
    </div>
  );
}
